import { useConfigs } from '@oncolens/oncolens-ui/lib/hooks/common/useConfig';
import TabbedCard from '@/components/Elements/Card/TabbedCard';
import RequestSection from '@/features/requests/components/NewRequest/RequestSection';
import React, { Fragment } from 'react';
import { isEmpty } from "lodash";
import { DiseaseDto } from '@/features/requests/components/NewRequest/Templates/osu/dtos/Disease';


type RequestDiseaseStagingRowProps = {
	label: string;
	displayText: string;
};

function RequestDiseaseStagingRow({ label, displayText }: RequestDiseaseStagingRowProps) {
  return (
    <>
      {label}: &nbsp;
      {displayText}
      <br />
    </>
  );
}

type DiseaseTabContentProps = {
	disease: DiseaseDto;
};

function DiseaseTabContent({ disease }: DiseaseTabContentProps) {
  //const configs = usePostCaseConfigs(["behavior"]);
  const configs = useConfigs(['behavior', 'setting', 'areas_of_metastasis', "recurrence", "recurrence_areas"]);
  const episode = disease.episodes.find((episode) => episode.isTargetEpisode);
  const clinical = '837e5af3-14e9-4325-93a4-67cb6f0da741';
  const pathological = '2b1ea756-5758-4b6b-ab30-2e7315024739';
  const postTherapy = 'b285796c-ea85-4428-bc4a-d00cdd73e12f';

  const clinicalStaging = episode?.staging?.stagingRowsAndValues?.find(
    (rowAndValues) => rowAndValues.stagingTimingId === clinical
  );
  const pathologicalStaging = episode?.staging?.stagingRowsAndValues?.find(
    (rowAndValues) => rowAndValues.stagingTimingId === pathological
  );
  const postTherapyStaging = episode?.staging?.stagingRowsAndValues?.find(
    (rowAndValues) => rowAndValues.stagingTimingId === postTherapy
  );

  const riskFactors = episode?.riskFactors || [];

  return (
    <div className="flex-col gap-5">
      <dl className="flex flex-wrap mb-6 gap-y-3 gap-x-10 xl:gap-x-20">
        <div>
          <dt className="mb-1 text-sm font-bold">Location</dt>
          <dd data-cy="disease-location" className="text-xs">
            {disease.cancerType.value} - {disease.cancerLocation.value}
          </dd>
        </div>
        <div>
          <dt className="mb-1 text-sm font-bold">Type</dt>
          <dd data-cy="disease-pathology" className="text-xs">{disease.cancerPathology.value}</dd>
        </div>
        <div>
          <dt className="mb-1 text-sm font-bold">Date of Initial Diagnosis</dt>
          <dd data-cy="disease-initial-diagnosis-date" className="text-xs">
            {disease?.initialDiagnosisDate?.toLocaleDateString() || '-'}
          </dd>
        </div>
      </dl>
      <dl className="flex flex-wrap mb-4 gap-y-3 gap-x-10 xl:gap-x-32">
        <div>
          <dt className="mb-1 text-sm font-bold">Status</dt>
          <dd data-cy="episode-status" className="text-xs">{episode?.diagnosisStatus || '-'}</dd>
        </div>
        <div>
          <dt className="mb-1 text-sm font-bold">Date</dt>
          <dd data-cy="episode-date" className="text-xs">
            {episode?.episodeDate ? new Date(episode?.episodeDate).toLocaleDateString() : '-'}
          </dd>
        </div>
        <div>
          <dt className="mb-1 text-sm font-bold">Behavior</dt>
          <dd data-cy="episode-behavior" className="text-xs">
            {configs?.behavior?.find((behavior) => behavior.value === episode?.cancerBehaviorId)
              ?.label || '-'}
          </dd>
        </div>
        <div>
          <dt className="mb-1 text-sm font-bold">Setting</dt>
          <dd data-cy="episode-setting" className="text-xs">
            {configs?.setting?.find((setting) => setting.value === episode?.cancerSettingId)
              ?.label || '-'}
          </dd>
        </div>
        {!isEmpty(episode?.areasOfMetastasis) && <div>
            <dt className="mb-1 text-sm font-bold">Areas of Metastasis</dt>
            <dd data-cy="episode-areas-of-metastasis" className="text-xs">
              {episode?.areasOfMetastasis.map((areaId) => configs?.areas_of_metastasis?.find((area) => area.value === areaId)
                ?.label || '-').join(", ")}
            </dd>
        </div> }
        {episode?.laterality && <div>
            <dt className="mb-1 text-sm font-bold">Laterality</dt>
            <dd data-cy="episode-laterality" className="text-xs">
              {episode?.laterality ?? '-'}
            </dd>
        </div> }
        {episode?.recurrenceExtentId && <div>
            <dt className="mb-1 text-sm font-bold">Recurrent With Extent</dt>
            <dd data-cy="episode-recurrence-extend" className="text-xs">
              {configs?.recurrence?.find((recurrence) => recurrence.value === episode?.recurrenceExtentId)
                ?.label || '-'}
            </dd>
        </div> }
        {!isEmpty(episode?.areasOfRecurrence) && <div>
            <dt className="mb-1 text-sm font-bold">Areas of Recurrence</dt>
            <dd data-cy="episode-areas-of-recurrence" className="text-xs">
              {episode?.areasOfRecurrence.map((areaId) => configs?.recurrence_areas?.find((area) => area.value === areaId)
                ?.label || '-').join(", ")}
            </dd>
        </div> }
      </dl>
      <dl className="flex flex-wrap mb-4 gap-y-3 gap-x-10 xl:gap-x-32">
        <div>
          <dt className="mb-1 text-sm font-bold">Staging</dt>
          <dd data-cy="episode-staging-data" className="text-xs">
            {clinicalStaging && (
              <RequestDiseaseStagingRow label="Clinical" displayText={episode?.staging?.clinicalTiming ?? '-'} />
            )}
            {pathologicalStaging && (
              <RequestDiseaseStagingRow
                label="Pathological"
                displayText={episode?.staging?.pathologicalTiming ?? '-'}
              />
            )}
            {postTherapyStaging && (
              <RequestDiseaseStagingRow
                label="Post-Therapy"
                displayText={episode?.staging?.postTherapyTiming ?? '-'}
              />
            )}
            {
              episode?.staging?.bclcStage && (
                <RequestDiseaseStagingRow
                  label="BCLC"
                  displayText={episode?.staging?.bclcStage}
                />
              )
            }
            {
              episode?.staging?.tumorSite && (
                <RequestDiseaseStagingRow
                  label="Tumor Site"
                  displayText={episode?.staging?.tumorSite}
                />
              )
            }
            {
              episode?.staging?.stagingDetails && (
                <RequestDiseaseStagingRow
                  label="Staging Details"
                  displayText={episode?.staging?.stagingDetails}
                />
              )
            }
            {!clinicalStaging &&
              !pathologicalStaging &&
              !postTherapyStaging &&
              !episode?.staging?.bclcStage &&
              !episode?.staging?.tumorSite &&
              !episode?.staging?.stagingDetails &&
              '-'}
          </dd>
        </div>
        <div>
          <dt className="mb-1 text-sm font-bold">Risk &amp; Prognostic Factors</dt>
          <dd className="text-xs" data-cy="episode-risk-factor">
            {riskFactors.length > 0
              ? riskFactors.map((riskFactor) => (
                <Fragment key={riskFactor.id}>
                  {riskFactor.description}
                  {riskFactor.value ? ` - ${riskFactor.value}` : ''}
                  {riskFactor.comments ? ` - ${riskFactor.comments}` : ''}
                  <br />
                </Fragment>
							  ))
              : '-'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

type RequestDiseasesProps = {
	diseases: DiseaseDto[] | undefined;
};

export default function RequestDiseases({ diseases }: RequestDiseasesProps) {
  const tabNames = diseases?.sort(
    (a, b) => a.isPrimary < b.isPrimary ? 1 : a.isPrimary > b.isPrimary ? -1 : 0
  )
    .map((disease) => disease.cancerType.value) || [];

  return (
    <RequestSection title="Disease">
      <TabbedCard tabNames={tabNames} outline>
        {(selected) => {
          const disease = diseases?.find((disease) => disease.cancerType.value === selected);
          return (
            <>
              <TabbedCard.Body>
                {disease && <DiseaseTabContent disease={disease} />}
              </TabbedCard.Body>
            </>
          );
        }}
      </TabbedCard>
    </RequestSection>
  );
}
